import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import Dialog from './ui/Dialog';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { WHATS_NEW_KEY } from './WhatsNew';

/** The key of the one-time Google Calendar message (the server remembers it per user: user_announcements_seen). */
export const WHATS_NEW_GCAL_KEY = 'whats_new_gcal_2026_11';

/** D-358: who sees the Google Calendar message - an OTE employee that has not connected a calendar yet. */
export function shouldShowGcalAnnouncement(i: {
  isOteEmployee: boolean; isStaff: boolean; seen: boolean | undefined; reportsSeen: boolean | undefined;
  connected: boolean | undefined; configured: boolean | undefined; closed: boolean;
}): boolean {
  return i.isOteEmployee && i.isStaff && i.seen === false && i.reportsSeen === true && i.configured === true && i.connected === false && !i.closed;
}

/**
 * D-358: a one-time "what's new" message about connecting Google Calendar - the same mechanism as the bug-report
 * message (D-341). Not for customers, test employees (the server answers "seen" for them, D-352) or whoever
 * already connected. "לחיבור עכשיו" opens the profile on the calendar card; "אחר כך" just closes it (seen).
 */
export default function WhatsNewGcal() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isStaff = user?.role === 'admin' || user?.role === 'agent';
  const isOteEmployee = user?.is_ote_employee === true;
  const [closed, setClosed] = useState(false);
  const eligible = isStaff && isOteEmployee;

  const gcalQ = useQuery({ queryKey: ['announcement', WHATS_NEW_GCAL_KEY], queryFn: () => api.getAnnouncement(WHATS_NEW_GCAL_KEY), enabled: eligible, staleTime: Infinity, retry: false });
  // one message at a time: this one waits until the bug-report message was seen
  const reportsQ = useQuery({ queryKey: ['announcement', WHATS_NEW_KEY], queryFn: () => api.getAnnouncement(WHATS_NEW_KEY), enabled: eligible, staleTime: Infinity, retry: false });
  const statusQ = useQuery({ queryKey: ['gcal', 'status'], queryFn: async () => (await api.getGcalStatus()).data ?? null, enabled: eligible, staleTime: 30_000, retry: false });

  const show = shouldShowGcalAnnouncement({
    isOteEmployee, isStaff, seen: gcalQ.data?.data?.seen, reportsSeen: reportsQ.data?.data?.seen,
    connected: statusQ.data?.connected, configured: statusQ.data?.configured, closed,
  });
  if (!show) return null;

  const markSeen = () => {
    setClosed(true);
    qc.setQueryData(['announcement', WHATS_NEW_GCAL_KEY], { success: true, data: { key: WHATS_NEW_GCAL_KEY, seen: true } });
    void api.markAnnouncementSeen(WHATS_NEW_GCAL_KEY).catch(() => undefined);
  };
  const connectNow = () => {
    markSeen();
    navigate('/profile?focus=gcal');
  };

  return (
    <Dialog
      open
      onClose={markSeen}
      title={t('whatsNewGcal.title')}
      size="md"
      footer={(
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={markSeen} data-testid="whatsnew-gcal-later">{t('whatsNewGcal.later')}</button>
          <button type="button" className="btn btn-primary" onClick={connectNow} data-testid="whatsnew-gcal-connect">
            <CalendarDays size={16} aria-hidden="true" /> {t('whatsNewGcal.connectNow')}
          </button>
        </div>
      )}
    >
      <div className="space-y-3 text-sm text-text-1" data-testid="whatsnew-gcal">
        <p>{t('whatsNewGcal.body')}</p>
        <p className="font-medium">{t('whatsNewGcal.howTitle')}</p>
        <ol className="list-decimal ps-5 space-y-1">
          {(['step1', 'step2', 'step3', 'step4', 'step5'] as const).map((k) => <li key={k}>{t(`whatsNewGcal.${k}`)}</li>)}
        </ol>
        <p>{t('whatsNewGcal.note')}</p>
      </div>
    </Dialog>
  );
}
